import {
  Checkbox,
  CloudArrowDownIcon,
  Tooltip,
  useCallback,
  useCanShow,
  useEffect,
  useExport,
  useMemo,
  useRef,
  useState,
} from "@";
import { exportDetailedReport } from "../utils/api";
import { useExportColumnsQuery } from "../hooks/useExportColumnsQuery";

// Stable reference so useExport's useCallback deps don't invalidate every render.
// Server returns the xlsx blob directly, so client-side header mapping is unused here.
const EMPTY_HEADERS = {};
const PANEL_ID = "export-columns-panel";

const ExportWithColumns = ({
  search,
  updateSearch,
  length,
  headerText,
  fileName,
}) => {
  const canExport = useCanShow("exportData");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const {
    data: columns = [],
    isLoading,
    isError,
    refetch,
  } = useExportColumnsQuery(open);

  const selectedKeys = useMemo(
    () => new Set(search.columns ?? []),
    [search.columns],
  );

  const setColumns = useCallback(
    (keysSet) =>
      updateSearch({
        values: { ...search, columns: Array.from(keysSet) },
      }),
    [search, updateSearch],
  );

  // Seed to "all selected" the first time columns arrive (search.columns === null).
  // A user-emptied selection ([]) is intentionally left alone.
  useEffect(() => {
    if (columns.length > 0 && search.columns === null) {
      updateSearch({
        values: { ...search, columns: columns.map((c) => c.key) },
      });
    }
  }, [columns, search, updateSearch]);

  const exportFn = useCallback(
    (page, pageSize, searchArg) =>
      exportDetailedReport(page, pageSize, searchArg),
    [],
  );

  const { isExporting, exporting } = useExport(
    exportFn,
    search,
    null,
    EMPTY_HEADERS,
    false,
    null,
    headerText,
    fileName,
  );

  // Close on outside click / Esc — subscribing to external system, effect is the right tool.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const allChecked = columns.length > 0 && selectedKeys.size === columns.length;
  const someChecked = selectedKeys.size > 0 && !allChecked;

  const toggleKey = (key) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setColumns(next);
  };

  const toggleAll = () => {
    if (allChecked) setColumns(new Set());
    else setColumns(new Set(columns.map((c) => c.key)));
  };

  const handleExport = async () => {
    await exporting();
    setOpen(false);
  };

  if (!canExport) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        data-tooltip-target="exportColumns"
        data-tooltip-placement="right"
        id="export-columns"
        type="button"
        className="btn-purple-900 my-2 w-fit disabled:cursor-not-allowed md:my-0"
        disabled={length < 1 || isExporting}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={() => setOpen((v) => !v)}
      >
        {isExporting ? (
          <div role="status">
            <svg
              aria-hidden="true"
              className="h-5 w-5 animate-spin fill-purple-900 text-gray-200"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          <CloudArrowDownIcon className="h-5 w-5" />
        )}
        <span className="ms-2 hidden md:inline-block">تصدير أكسل</span>
      </button>
      <Tooltip className="dark:bg-gray-600" title="تصدير" id="exportColumns" />

      {open && (
        <div
          id={PANEL_ID}
          className="absolute start-0 top-full z-20 mt-2 w-72 rounded-2xl border border-gray-200 bg-white text-sm text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
        >
          {/* header */}
          <div className="flex items-center justify-between rounded-t-xl border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-600">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              الأعمدة
            </h3>
            <label className="checkbox-label text-xs">
              <input
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                type="checkbox"
                className="input me-2 h-6 w-5 rounded-lg"
                checked={allChecked}
                onChange={toggleAll}
                disabled={isLoading || columns.length === 0}
              />
              تحديد الكل
            </label>
          </div>

          {/* body */}
          <div className="max-h-64 overflow-auto px-3 py-2">
            {isLoading && (
              <p className="py-4 text-center text-gray-500 dark:text-gray-400">
                جارٍ التحميل...
              </p>
            )}
            {isError && (
              <div className="flex flex-col items-center gap-2 py-4">
                <p className="text-red-500">تعذّر تحميل الأعمدة</p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="text-purple-900 underline dark:text-purple-400"
                >
                  إعادة المحاولة
                </button>
              </div>
            )}
            {!isLoading &&
              !isError &&
              columns.map((col) => (
                <div key={col.key} className="mb-2 last:mb-0">
                  <Checkbox
                    inputClassName="h-6"
                    label={col.header}
                    input={{
                      checked: selectedKeys.has(col.key),
                      onChange: () => toggleKey(col.key),
                    }}
                  />
                </div>
              ))}
          </div>

          {/* footer */}
          <div className="flex justify-end rounded-b-xl border-t border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-600">
            <button
              type="button"
              className="btn-green w-full disabled:cursor-not-allowed disabled:opacity-60"
              disabled={selectedKeys.size === 0 || isExporting || isLoading}
              onClick={handleExport}
            >
              <CloudArrowDownIcon className="h-5 w-5" />
              <span className="ms-2">تصدير</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportWithColumns;
